<template>
  <div class="congruence-similarity-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg viewBox="0 0 800 300" class="canvas-svg">
          <!-- Text Overlays -->
          <text x="200" y="40" class="stage-label" fill="#64748b" text-anchor="middle">原图形 (Ref)</text>
          <text x="600" y="40" class="stage-label" fill="#64748b" text-anchor="middle">变换图形 (Target)</text>

          <!-- SVG Grid / Coordinate sys -->
          <!-- Original Triangle ABC -->
          <g transform="translate(200, 180)">
             <polygon :points="refPoints" fill="rgba(13, 104, 255, 0.2)" stroke="#0D68FF" stroke-width="2" />
             <!-- Vertices -->
             <circle cx="0" cy="-60" r="4" fill="#0D68FF" />
             <circle cx="-50" cy="40" r="4" fill="#0D68FF" />
             <circle cx="60" cy="40" r="4" fill="#0D68FF" />
             <text x="0" y="-70" fill="#fff" text-anchor="middle">A</text>
             <text x="-60" y="55" fill="#fff" text-anchor="middle">B</text>
             <text x="70" y="55" fill="#fff" text-anchor="middle">C</text>
          </g>

          <!-- Target Triangle A'B'C' -->
          <g :transform="targetTransform">
             <polygon :points="refPoints" fill="rgba(42, 245, 152, 0.2)" stroke="#2AF598" stroke-width="2" />
             <circle cx="0" cy="-60" r="4" fill="#2AF598" />
             <circle cx="-50" cy="40" r="4" fill="#2AF598" />
             <circle cx="60" cy="40" r="4" fill="#2AF598" />
             <text x="0" y="-70" fill="#fff" text-anchor="middle">A'</text>
             <text x="-60" y="55" fill="#fff" text-anchor="middle">B'</text>
             <text x="70" y="55" fill="#fff" text-anchor="middle">C'</text>
          </g>
        </svg>
        
        <div class="hud-center">
            <div class="status-badge" :class="{ 'sim': k !== 1, 'con': k === 1 }">
              状态: {{ k === 1 ? '全等 (Congruent)' : '相似 (Similar)' }}
            </div>
        </div>
      </div>

      <div class="compact-controls">
         <div class="control-card glass">
            <div class="control-vertical-group">
               <div class="param-row">
                  <div class="s-item">
                     <label>相似比 k: {{ k.toFixed(2) }}</label>
                     <el-slider v-model="k" :min="0.5" :max="2.0" :step="0.1" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item">
                     <label>旋转 (度): {{ rotation }}°</label>
                     <el-slider v-model="rotation" :min="0" :max="360" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item">
                     <label>平移 X (Δx): {{ translateX }} px</label>
                     <el-slider v-model="translateX" :min="-200" :max="200" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item">
                     <label>平移 Y (Δy): {{ translateY }} px</label>
                     <el-slider v-model="translateY" :min="-100" :max="100" input-size="mini"></el-slider>
                  </div>
               </div>
            </div>
         </div>

         <div class="discovery-card-compact" v-latex>
            <i>🔍</i>
            <p><strong>互动观察：</strong>当 k = 1 时，图形发生平移或旋转，但大小和形状不变，即为<strong>全等</strong>。当 k ≠ 1 时，图形改变了大小，但保持形状不变（对应边成比例，对应角相等），即为<strong>相似</strong>。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'CongruenceSimilarityViz',
  data() {
    return {
      k: 1.0,
      rotation: 0,
      translateX: 0,
      translateY: 0,
      refPoints: "0,-60 -50,40 60,40"
    }
  },
  computed: {
    targetTransform() {
      const bx = 600 + this.translateX;
      const by = 180 + this.translateY;
      return `translate(${bx}, ${by}) rotate(${this.rotation}) scale(${this.k})`;
    }
  }
}
</script>

<style scoped lang="scss">
.congruence-similarity-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #10b981; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.main-layout { display: flex; gap: 12px; height: 100%; min-height: 0; flex-direction: column; }
.simulation-stage { 
  flex: 1; min-height: 200px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .canvas-svg { width: 100%; height: 100%; }
  .hud-center {
    position: absolute; top: 15px; width: 100%; text-align: center; display: flex; justify-content: center;
    .status-badge {
      background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); padding: 4px 16px; border-radius: 20px; color: #fff; font-weight: bold; font-size: 13px; backdrop-filter: blur(4px);
      &.con { background: rgba(13,104,255,0.2); border-color: #0D68FF; color: #60a5fa; }
      &.sim { background: rgba(42,245,152,0.2); border-color: #2AF598; color: #34d399; }
    }
  }
}
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: -5px; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { font-style: normal; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
